import { getMimeType } from "./../../utils/mime-type";
import { saveAs } from "node_modules/file-saver";
import { IDocument, IDocumentDownloadRequestObject, IGetDocumentsParams } from "./../../models/document";

import { Injectable } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { BaseHttpService } from "./base.http.service";
import { AppConfig } from "src/app/app.config";
import { IEnvironmentConfig } from "src/app/models/environmentConfig";
import { switchMap } from "rxjs/operators";

@Injectable()
export class DocumentLibraryService extends BaseHttpService {
    private fileName: string;
    private fileType: string;

    constructor(
        private http: HttpClient,
        private appConfig: AppConfig,
    ) {
        super();
    }

    get envSettings(): IEnvironmentConfig {
        return this.appConfig.getEnvironmentConfig();
    }

    public getDocuments(payload: IGetDocumentsParams): Observable<any> {
        const dateString = payload.toDate.toISOString().split("T")[0];
        let queryString = `?date=${dateString}`;
        payload.entityIds.forEach((item, index) => {
            queryString = queryString + "&entities=" + item.EntityId;
        });
        const url = `${this.envSettings.webApiUrl}documents/entities${queryString}`;
        return this.http.get(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public downloadDocument(payload: IDocumentDownloadRequestObject): Observable<any> {
        // This is insane that we decided that the service should have state related to the document downloading
        this.fileName = payload.fileName;
        this.fileType = payload.fileType;
        const token = encodeURIComponent(payload.token);
        const url = `${this.envSettings.webApiUrl}documents/?token=${token}`;
        return this.http.get(url, {
            observe: "body",
            responseType: "blob",
            headers: this.getHttpHeaders(),
        });
    }

    public downloadDocumentFromToken(token: string) {
        const urlEncodedToken = encodeURIComponent(token);
        const url = `${this.envSettings.webApiUrl}documents?token=${urlEncodedToken}`;
        return this.http
            .get(url, {
                observe: "response",
                responseType: "blob",
                headers: this.getHttpHeaders(),
            })
            .pipe(
                switchMap((response: HttpResponse<Blob>) => {
                    const contentDisposition = response.headers.get('content-disposition');
                    const fileName = this.parseContentDisposition(contentDisposition);
                    
                    return of({
                        fileName: fileName,
                        blob: response.body,
                    });
                }),
            );
    }

    private parseContentDisposition(contentDisposition: string | null): string | null {
        if (!contentDisposition) {
            return null;
        }
        
        const matches = /filename=(?:"([^"]*)"|(.*?)(;|$))/i.exec(contentDisposition);
        return matches && (matches[1] || matches[2]) ? (matches[1] || matches[2]) : null;
    }

    public writeFileFromBlob(reportBlob: any) {
        const observable = new Observable((observer) => {
            const type = getMimeType(this.fileType);
            const blob = new Blob([reportBlob], { type: type });
            saveAs(blob, this.fileName + this.fileType);
            observer.next();
        });
        return observable;
    }

    public getProductDisclosureDocuments(portfolioIds: number[]) {
        let queryString = portfolioIds.map((id) => `portfolioIds=${id}`).join("&");
        const url = `${this.envSettings.webApiUrl}documents/portfolios/disclosures?${queryString}`;
        return this.http.get<IDocument[]>(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }

    public getTermsAndConditionDocuments(portfolioIds: number[]) {
        let queryString = portfolioIds.map((id) => `portfolioIds=${id}`).join("&");
        const url = `${this.envSettings.webApiUrl}documents/portfolios/terms-and-conditions?${queryString}`;
        return this.http.get<IDocument[]>(url, {
            observe: "body",
            responseType: "json",
            headers: this.getHttpHeaders()
        });
    }
}
